import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import { convertToVwMob } from '../../../Utils/responsive';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import DSDeleteConfirmModal from '../../../components/designSystem/DSDeleteConfirmModal';
import CreatePostModalMob from './CreatePostModal';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import ProjectImage from '../../../components/common/ProjectImage';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectProjects, selectProjectsStatus, selectGetProjectStatus, selectSelectedProject, selectPosts, selectSearchPostsStatus } from '../../../Module/Project/Presentation/projectSelectors';
import { getProject, searchProjects, searchProjectPosts, updateProjectPost, deleteProjectPost, createProjectPost } from '../../../Module/Project/Presentation/projectActions';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { PATHS } from '../../../routes/paths';
import { extractImageSlots, restoreImageSlots } from '../../../Utils/postImageSlots';
import { sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';

import projectIcon from '../../../assets/Icon/sidbar/project_icon.png';
import postActiveIcon from '../../../assets/Icon/sidbar/post_active_icon.png';
import sponsorIcon from '../../../assets/Icon/sidbar/sponsor_icon.png';
import payIcon from '../../../assets/Icon/sidbar/pay_icon.png';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import kakaoExampleImg from '../../../assets/img/share_modal/kakao_example.png';
import emailExampleImg from '../../../assets/img/share_modal/email_example.png';
import { useTranslation } from 'react-i18next';
import { openMailClient } from '../../../Utils/mail';

interface PostManagementMobProps {
  hasPosts: boolean;
}

const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const KAKAO_SDK_SRC = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      if (!base64) {
        reject(new Error('이미지 인코딩에 실패했습니다.'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('이미지 인코딩 중 오류가 발생했습니다.'));
    reader.readAsDataURL(file);
  });

const PostManagementMob: React.FC<PostManagementMobProps> = ({ hasPosts: propHasPosts }) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectSession);
  const projects = useAppSelector(selectProjects);
  const projectsStatus = useAppSelector(selectProjectsStatus);
  const selectedProject = useAppSelector(selectSelectedProject);
  const getStatus = useAppSelector(selectGetProjectStatus);
  const posts = useAppSelector(selectPosts);
  const searchPostsStatus = useAppSelector(selectSearchPostsStatus);

  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;

  // 세션 정보에서 userId 가져오기
  const userId = useMemo(() => {
    return (session as any)?.user?.id || window.localStorage.getItem('missionfund.userId') || '';
  }, [session]);

  useEffect(() => {
    if (projectId) {
      // 이미 로딩 중이거나 현재 프로젝트가 요청하려는 프로젝트와 같으면 중단
      if (getStatus === 'loading' || selectedProject?.project_id === projectId) {
        // 프로젝트는 있는데 포스트가 아직 로딩 전이면 포스트만 요청
        if (selectedProject?.project_id === projectId && searchPostsStatus === 'idle' && posts.length === 0) {
          dispatch(
            searchProjectPosts({
              limit: 1000,
              params: [{ fld: 'projectId', op: 'eq', val: projectId }],
              sort: 'created_at:desc',
            }),
          );
        }
        return;
      }
      dispatch(getProject({ project_id: projectId }));
      dispatch(
        searchProjectPosts({
          limit: 1000,
          params: [{ fld: 'projectId', op: 'eq', val: projectId }],
          sort: 'created_at:desc',
        }),
      );
    } else if (userId && projects.length === 0 && projectsStatus !== 'loading') {
      dispatch(
        searchProjects({
          limit: 100,
          params: [{ fld: 'user_id', op: 'eq', val: userId }],
          sort: 'created_at:desc',
        }),
      );
    }
  }, [dispatch, projectId, userId, projects.length, projectsStatus, getStatus, selectedProject?.project_id, searchPostsStatus, posts.length]);

  const currentProject = useMemo(() => {
    if (projectId) {
      if (selectedProject?.project_id === projectId) return selectedProject;
      return projects.find((p) => String(p.project_id ?? p._id ?? '') === projectId);
    }
    return selectedProject || projects[0]; // 없으면 첫번째 프로젝트 사용 (유저가 선택한게 없다면)
  }, [projectId, selectedProject, projects]);

  const isProjectDetailPending =
    Boolean(projectId) &&
    selectedProject?.project_id !== projectId &&
    (getStatus === 'idle' || getStatus === 'loading');
  const isProjectListPending =
    !projectId &&
    Boolean(userId) &&
    projects.length === 0 &&
    (projectsStatus === 'idle' || projectsStatus === 'loading');
  const activeProjectId = projectId || currentProject?.project_id;
  const isPostsPending =
    Boolean(activeProjectId) &&
    posts.length === 0 &&
    (searchPostsStatus === 'idle' || searchPostsStatus === 'loading');
  const isLoading = isProjectDetailPending || isProjectListPending || isPostsPending;

  // 첫번째 프로젝트 로드 후 포스트 요청
  useEffect(() => {
    if (!projectId && currentProject?.project_id && searchPostsStatus === 'idle' && posts.length === 0) {
      dispatch(
        searchProjectPosts({
          limit: 1000,
          params: [{ fld: 'projectId', op: 'eq', val: currentProject.project_id }],
          sort: 'created_at:desc',
        }),
      );
    }
  }, [projectId, currentProject?.project_id, searchPostsStatus, posts.length, dispatch]);

  const projectTitle = currentProject?.title || t('common.projectTitlePlaceholder');
  const realPosts = posts;
  const hasPosts = realPosts.length > 0;

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [isShareBottomSheetOpen, setIsShareBottomSheetOpen] = useState(false);
  const [sharePreviewType, setSharePreviewType] = useState<'kakao' | 'email' | null>(null);
  const [expandedPostIds, setExpandedPostIds] = useState<Set<string>>(new Set());
  const kakaoSdkLoadingRef = useRef<Promise<any> | null>(null);

  const selectedPost = useMemo(() => {
    if (!selectedPostId) return null;
    return realPosts.find((p) => String((p as any)?._id ?? p.projectpost_id) === selectedPostId) || null;
  }, [realPosts, selectedPostId]);

  const buildSharePostTitle = useCallback(() => {
    const sourcePost = selectedPost ?? realPosts[0];
    const htmlContent =
      typeof (sourcePost as any)?.contents === 'string'
        ? (sourcePost as any).contents
        : typeof sourcePost?.update_message === 'string'
          ? sourcePost.update_message
          : '';
    const textContent = htmlContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (textContent) {
      const maxLength = 40;
      return textContent.length > maxLength ? `${textContent.slice(0, maxLength)}...` : textContent;
    }
    return t('page.postManagement.shareModal.defaultTitle');
  }, [realPosts, selectedPost, t]);

  const postShareUrl = useMemo(() => {
    const baseUrl = `https://go.MissionFund.org/${currentProject?.project_id || ''}`;
    return selectedPostId ? `${baseUrl}?postId=${selectedPostId}` : baseUrl;
  }, [currentProject?.project_id, selectedPostId]);

  const toggleExpand = useCallback((postId: string) => {
    setExpandedPostIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  }, []);

  // 새소식 작성 모달 상태
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'none'>('image');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [postContent, setPostContent] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);

  const handleMoreClick = (postId: string) => {
    setSelectedPostId(postId);
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
    setSelectedPostId(null);
  };

  const handleShare = () => {
    handleCloseBottomSheet();
    setIsShareBottomSheetOpen(true);
  };

  const handleCloseShareBottomSheet = () => {
    setIsShareBottomSheetOpen(false);
  };

  const ensureKakaoSdk = useCallback(async () => {
    const kakaoWindow = window as any;

    if (kakaoWindow.Kakao) {
      if (!kakaoWindow.Kakao.isInitialized()) {
        kakaoWindow.Kakao.init(KAKAO_KEY);
      }
      return kakaoWindow.Kakao;
    }

    if (kakaoSdkLoadingRef.current) {
      return kakaoSdkLoadingRef.current;
    }

    kakaoSdkLoadingRef.current = new Promise((resolve, reject) => {
      const finalize = () => {
        if (!kakaoWindow.Kakao) {
          reject(new Error('Kakao SDK not loaded'));
          return;
        }
        if (!kakaoWindow.Kakao.isInitialized()) {
          kakaoWindow.Kakao.init(KAKAO_KEY);
        }
        resolve(kakaoWindow.Kakao);
      };

      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${KAKAO_SDK_SRC}"]`);
      if (existingScript) {
        if (kakaoWindow.Kakao) {
          finalize();
          return;
        }
        existingScript.addEventListener('load', finalize, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = KAKAO_SDK_SRC;
      script.crossOrigin = 'anonymous';
      script.addEventListener('load', finalize, { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK')), { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      kakaoSdkLoadingRef.current = null;
      throw error;
    });

    return kakaoSdkLoadingRef.current;
  }, []);

  useEffect(() => {
    void ensureKakaoSdk().catch(() => {});
  }, [ensureKakaoSdk]);

  const runKakaoShare = async () => {
    const shareTitle = buildSharePostTitle();
    try {
      const kakao = await ensureKakaoSdk();
      shareToKakao(kakao, shareTitle, postShareUrl);
    } catch (error) {
      console.error('Failed to run Kakao share:', error);
      // eslint-disable-next-line no-alert
      alert('카카오 공유를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const shareToKakao = (kakao: any, title: string, url: string) => {
    const sendDefault = kakao?.Share?.sendDefault ?? kakao?.Link?.sendDefault;
    if (!sendDefault) {
      throw new Error('Kakao share API is unavailable');
    }

    sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        description: `${t('common.shareModal.descLine1')} ${t('common.shareModal.descLine2')}`,
        imageUrl: 'https://missionfund.org/static/media/main_logo.png',
        link: {
          mobileWebUrl: url,
          webUrl: url,
        },
      },
      buttons: [
        {
          title: t('common.shareModal.buttonDetail'),
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      ],
    });
  };

  const runEmailShare = () => {
    const shareTitle = buildSharePostTitle();
    const subject = t('common.shareModal.emailSubject', { title: shareTitle });
    const body = t('page.postManagement.shareModal.emailBody', { title: shareTitle, url: postShareUrl });
    openMailClient({ subject, body });
  };

  const handleKakaoShare = () => {
    setIsShareBottomSheetOpen(false);
    setSharePreviewType('kakao');
  };

  const handleEmailShare = () => {
    setIsShareBottomSheetOpen(false);
    setSharePreviewType('email');
  };

  const handleCloseSharePreview = () => {
    setSharePreviewType(null);
  };

  const handleBackSharePreview = () => {
    setSharePreviewType(null);
    setIsShareBottomSheetOpen(true);
  };

  const handleConfirmSharePreview = () => {
    if (sharePreviewType === 'kakao') {
      runKakaoShare();
    } else if (sharePreviewType === 'email') {
      runEmailShare();
    }
    setSharePreviewType(null);
  };

  const handleCopyLink = async () => {
    const url = postShareUrl;
    try {
      await navigator.clipboard.writeText(url);
      // eslint-disable-next-line no-alert
      alert(t('common.messages.linkCopied'));
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      // eslint-disable-next-line no-alert
      alert(t('common.messages.linkCopied'));
    }
    handleCloseShareBottomSheet();
  };

  const handleEdit = () => {
    const post = realPosts.find(p => String((p as any)?._id ?? p.projectpost_id) === selectedPostId);
    if (post) {
      setIsEditMode(true);

      const rawContent = typeof (post as any)?.contents === 'string'
        ? (post as any).contents
        : typeof post.update_message === 'string'
          ? post.update_message
          : '';

      // 슬롯 플레이스홀더를 다시 에디터용 이미지 태그로 복원
      setPostContent(restoreImageSlots(rawContent, post));

      setPreviewUrl(post.image_url || null);
      setVideoUrl(post.video_url ? String(post.video_url) : '');
      setMediaType(post.video_url ? 'video' : (post.image_url ? 'image' : 'none'));
      setIsCreateModalOpen(true);
      setCurrentStep(1);
    }
    handleCloseBottomSheet();
  };

  const handleDelete = () => {
    handleCloseBottomSheet();
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPostId || !currentProject?.project_id) return;

    try {
      setIsDeletingPost(true);
      await dispatch(
        deleteProjectPost({
          projectpost_id: selectedPostId,
          project_id: currentProject.project_id,
          partner: currentProject.partner || 'OMF',
        }),
      ).unwrap();

      setIsDeleteModalOpen(false);
      setSelectedPostId(null);
      // 알림 표시
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (e: any) {
      alert(e || t('page.postManagement.alerts.deleteFail'));
    } finally {
      setIsDeletingPost(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  // 새소식 작성 모달 핸들러
  const handleOpenCreateModal = () => {
    setIsEditMode(false);
    setSelectedPostId(null);
    setIsCreateModalOpen(true);
    setCurrentStep(1);
    setMediaType('image');
    setSelectedFile(null);
    setPreviewUrl(null);
    setVideoUrl('');
    setPostContent('');
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setIsEditMode(false);
    setSelectedPostId(null);
    setCurrentStep(1);
    setMediaType('image');
    setSelectedFile(null);
    setPreviewUrl(null);
    setVideoUrl('');
    setPostContent('');
    setIsUpdatingPost(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveMainMedia = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
  };

  const handleNextStep = async () => {
    if (currentStep === 3) {
      const videoUrlTrimmed = videoUrl.trim();
      if (isEditMode) {
        if (!selectedPostId || !currentProject?.project_id) return;

        const existingPost = realPosts.find((p) => String((p as any)?._id ?? p.projectpost_id) === selectedPostId);
        if (!existingPost) return;

        try {
          setIsUpdatingPost(true);
          const partner = currentProject.partner || 'OMF';

          // 서버 전송 직전 HTML 정리 후 본문 인라인 이미지 추출/슬롯 할당
          const sanitizedPostContent = sanitizeProjectDescHtml(postContent);
          const { update_message, ...imageSlots } = extractImageSlots(sanitizedPostContent);

          await dispatch(
            updateProjectPost({
              projectpost_id: selectedPostId,
              project_id: currentProject.project_id,
              partner,
              update_message,
              video_url: mediaType === 'video' ? videoUrlTrimmed : '',
              image_url: previewUrl && !selectedFile ? previewUrl : (existingPost.image_url || ''),
              // 새 이미지 파일이 선택된 경우 (대표 이미지)
              image: mediaType === 'image' && selectedFile ? await readFileAsBase64(selectedFile) : undefined,
              // 기존 이미지 URL 유지 (새 base64가 없는 경우)
              image2_url: existingPost.image2_url || '',
              image3_url: existingPost.image3_url || '',
              image4_url: existingPost.image4_url || '',
              image5_url: existingPost.image5_url || '',
              ...imageSlots, // image2, image3... (base64)가 있으면 위 URL보다 우선순위가 높음
              image_desc: existingPost.image_desc || '',
              image2_desc: existingPost.image2_desc || '',
              image3_desc: existingPost.image3_desc || '',
              image4_desc: existingPost.image4_desc || '',
              image5_desc: existingPost.image5_desc || '',
              authentication_by: 'WEB',
              to_sponsor: existingPost.to_sponsor ?? false,
              emoticon01: existingPost.emoticon01 ?? 0,
              emoticon02: existingPost.emoticon02 ?? 0,
              emoticon03: existingPost.emoticon03 ?? 0,
              emoticon04: existingPost.emoticon04 ?? 0,
              emoticon05: existingPost.emoticon05 ?? 0,
              cheermsg: (existingPost as any).cheermsg || [],
            }),
          ).unwrap();

          handleCloseCreateModal();
          setShowSuccessAlert(true);

          // 리스트 새로고침
          dispatch(
            searchProjectPosts({
              limit: 1000,
              params: [{ fld: 'projectId', op: 'eq', val: currentProject.project_id }],
              sort: 'created_at:desc',
            }),
          );

          setTimeout(() => setShowSuccessAlert(false), 3000);
        } catch (e: any) {
          alert(e || t('page.postManagement.alerts.updateFail'));
        } finally {
          setIsUpdatingPost(false);
        }
        return;
      }

      if (!currentProject?.project_id) {
        alert(t('page.postManagement.alerts.selectProject'));
        return;
      }

      try {
        setIsUpdatingPost(true);
        const partner = currentProject.partner || 'OMF';
        const imageBase64 =
          mediaType === 'image' && selectedFile ? await readFileAsBase64(selectedFile) : undefined;

        // 서버 전송 직전 HTML 정리 후 본문 인라인 이미지 추출/슬롯 할당
        const sanitizedPostContent = sanitizeProjectDescHtml(postContent);
        const { update_message, ...imageSlots } = extractImageSlots(sanitizedPostContent);

        // 1. 게시물 생성
        await dispatch(createProjectPost({
          project_id: currentProject.project_id,
          partner,
          update_message,
          video_url: mediaType === 'video' ? videoUrlTrimmed : '',
          image2_desc: '',
          image3_desc: '',
          image4_desc: '',
          image5_desc: '',
          image: imageBase64,
          ...imageSlots, // image2, image3... (base64)
          authentication_by: 'WEB',
          to_sponsor: false,
        })).unwrap();

        handleCloseCreateModal();
        setShowSuccessAlert(true);

        // 리스트 새로고침
        dispatch(
          searchProjectPosts({
            limit: 1000,
            params: [{ fld: 'projectId', op: 'eq', val: currentProject.project_id }],
            sort: 'created_at:desc',
          }),
        );

        // 3초 후 알림 자동 숨김
        setTimeout(() => {
          setShowSuccessAlert(false);
        }, 3000);
      } catch (e: any) {
        alert(e || t('page.postManagement.alerts.createFail'));
      } finally {
        setIsUpdatingPost(false);
      }
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleMediaTypeChange = (type: 'image' | 'video' | 'none') => {
    setMediaType(type);
    if (type === 'none') {
      setPreviewUrl(null);
      setSelectedFile(null);
      setVideoUrl('');
    }
    if (type === 'image') {
      setVideoUrl('');
    }
    if (type === 'video') {
      setPreviewUrl(null);
      setSelectedFile(null);
    }
  };

  const handleContentChange = (content: string) => {
    setPostContent(content);
  };

  if (isLoading) {
    return (
      <MobilePageLayout title={t('page.postManagement.pageTitle')} showBottomNav={!isCreateModalOpen} hideHeader={isCreateModalOpen}>
        <ProjectDetailHeader title={projectTitle} projectId={activeProjectId} variant="mobile" />
        <DSLoading />
      </MobilePageLayout>
    );
  }

  return (
    <MobilePageLayout title={t('page.postManagement.pageTitle')} showBottomNav={!isCreateModalOpen} hideHeader={isCreateModalOpen}>
      <Container>
        <HeaderSection>
        <ProjectDetailHeader title={projectTitle} projectId={activeProjectId} variant="mobile" />
          {showSuccessAlert && (
            <SuccessAlert>
              <CheckIconImage src="/Image/posts/check1.png" alt={t('common.status.complete')} />
              {t('page.postManagement.alerts.updateSuccess')}
            </SuccessAlert>
          )}
          <PageTitle>{t('page.postManagement.pageTitle')}</PageTitle>
        </HeaderSection>

        {!hasPosts ? (
          // 게시물이 없는 경우 (원래 디자인)
          <>
            <ContentCard>
              <CardContent>
                <CardText>
                  <HighlightText>{t('page.postManagement.empty.highlight')}</HighlightText>
                  <br />
                  {t('page.postManagement.empty.line1')} {t('page.postManagement.empty.line2')}
                </CardText>
                <PrimaryButton onClick={handleOpenCreateModal}>
                  <NoPlusIcon src="/Image/posts/plusIcon.png" alt={t('page.postManagement.actions.create')} />
                  {t('page.postManagement.actions.create')}
                </PrimaryButton>
              </CardContent>
            </ContentCard>

            <InfoBanner>
              <InfoIconLarge src="/Image/posts/idea_01.png" alt={t('page.postManagement.ideaAlt')} />
              {t('page.postManagement.infoBanner')}
            </InfoBanner>
          </>
        ) : (
          // 게시물이 있는 경우
          <>
            <YellowBanner>
              <BannerContent>
                <BannerIcon src="/Image/posts/idea_01.png" alt={t('page.postManagement.ideaAlt')} />
                <BannerText>{t('page.postManagement.banner.messageMobile')}</BannerText>
              </BannerContent>
              <ShareButton
                onClick={() => {
                  const latestPostId = String((realPosts[0] as any)?._id ?? realPosts[0]?.projectpost_id ?? '');
                  setSelectedPostId(latestPostId || null);
                  setIsShareBottomSheetOpen(true);
                }}
              >
                <ShareIcon src="/Image/posts/shareIcon.png" alt={t('common.empty.shareAlt')} />
                {t('page.postManagement.actions.shareRecent')}
              </ShareButton>
            </YellowBanner>

            <PostList>
              {realPosts.map((post, idx) => {
                const postId = String((post as any)?._id ?? post.projectpost_id ?? idx);
                const isExpanded = expandedPostIds.has(postId);
                const postHtml =
                  typeof (post as any)?.contents === 'string'
                    ? (post as any).contents
                    : typeof post.update_message === 'string'
                      ? post.update_message
                      : '';

                // 우선순위: image_url > image2~5_url > HTML 내 첫 번째 이미지
                let displayImage = post.image_url || post.image2_url || post.image3_url || post.image4_url || post.image5_url;

                if (!displayImage && postHtml) {
                  const imgMatch = postHtml.match(/<img[^>]+src="([^">]+)"/);
                  if (imgMatch) {
                    displayImage = imgMatch[1];
                  }
                }

                return (
                  <PostCard key={postId}>
                    <PostHeader>
                      <PostDate>{post.created_at ? String(post.created_at).slice(0, 16).replaceAll('-', '.').replace('T', ' ') : ''}</PostDate>
                      <MoreButton onClick={() => handleMoreClick(postId)}>⋮</MoreButton>
                    </PostHeader>
                    {displayImage && (
                      <PostImage
                        as={ProjectImage}
                        idOrUrl={displayImage}
                        alt=""
                      />
                    )}
                    <PostContent>
                      <PostText isExpanded={isExpanded}>
                        <SafeHtmlViewer html={postHtml} postData={post} />
                      </PostText>
                    </PostContent>
                    <ButtonWrapper>
                      <DetailButton onClick={() => toggleExpand(postId)}>
                        {isExpanded ? t('page.postManagement.toggle.collapse') : t('page.postManagement.toggle.expand')}
                        <DownIcon
                          src="/Image/posts/downIcon.png"
                          alt={isExpanded ? t('page.postManagement.toggle.collapseAlt') : t('page.postManagement.toggle.expandAlt')}
                          style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                        />
                      </DetailButton>
                    </ButtonWrapper>
                  </PostCard>
                );
              })}
            </PostList>

            <FixedAddButton onClick={handleOpenCreateModal}>
              <PlusIcon src="/Image/posts/plusIcon.png" alt={t('common.actions.add')} />
              {t('page.postManagement.actions.create')}
            </FixedAddButton>
          </>
        )}
      </Container>

      <DSBottomSheet open={isBottomSheetOpen} onClose={handleCloseBottomSheet} variant="transparent">
        <BottomSheetWrapper>
          <BottomSheetContent>
            <BottomSheetButton onClick={handleShare}>
              {t('common.actions.share')}
            </BottomSheetButton>
            <ButtonDivider />
            <BottomSheetButton onClick={handleEdit}>
              {t('common.actions.edit')}
            </BottomSheetButton>
            <ButtonDivider />
            <BottomSheetButton onClick={handleDelete}>
              {t('common.actions.delete')}
            </BottomSheetButton>
          </BottomSheetContent>
          <CancelContainer>
            <CancelButton onClick={handleCloseBottomSheet}>
              {t('common.actions.cancel')}
            </CancelButton>
          </CancelContainer>
        </BottomSheetWrapper>
      </DSBottomSheet>

      <DSDeleteConfirmModal
        open={isDeleteModalOpen}
        itemName={t('page.postManagement.itemName')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeletingPost}
      />

      <CreatePostModalMob
        isOpen={isCreateModalOpen}
        isEditMode={isEditMode}
        currentStep={currentStep}
        mediaType={mediaType}
        previewUrl={previewUrl}
        videoUrl={videoUrl}
        postContent={postContent}
        isLoading={isUpdatingPost}
        onClose={handleCloseCreateModal}
        onFileSelect={handleFileSelect}
        onNextStep={handleNextStep}
        onPrevStep={handlePrevStep}
        onMediaTypeChange={handleMediaTypeChange}
        onContentChange={handleContentChange}
        onVideoUrlChange={setVideoUrl}
        onRemoveMainMedia={handleRemoveMainMedia}
      />

      <DSBottomSheet open={isShareBottomSheetOpen} onClose={handleCloseShareBottomSheet} padding="0">
        <ShareBottomSheetWrapper>
          <ShareHeader>
            <TitleRow>
              <ShareTitle>{t('page.postManagement.shareModal.title')}</ShareTitle>
              <CloseButton onClick={handleCloseShareBottomSheet} aria-label="close">
                ✕
              </CloseButton>
            </TitleRow>
            <ShareDescription>
              {t('common.shareModal.descLine1')}
              <br />
              {t('common.shareModal.descLine2')}
            </ShareDescription>
          </ShareHeader>
          <ShareOptionsContainer>
            <ShareOption onClick={handleKakaoShare}>
              <KakaoIconBox>
                <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
              </KakaoIconBox>
              <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
            </ShareOption>

            <ShareOption onClick={handleEmailShare}>
              <EmailIconBox>
                <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
              </EmailIconBox>
              <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
            </ShareOption>
          </ShareOptionsContainer>

          <CopyLinkButtonContainer>
            <CopyLinkButton onClick={handleCopyLink}>
              <CopyIcon />
              {t('common.shareModal.copyLink')}
            </CopyLinkButton>
          </CopyLinkButtonContainer>
        </ShareBottomSheetWrapper>
      </DSBottomSheet>
      <DSBottomSheet open={Boolean(sharePreviewType)} onClose={handleCloseSharePreview} padding="0">
        {sharePreviewType && (
          <SharePreviewSheet>
            <SharePreviewHeader>
              <SharePreviewTitleRow>
                <SharePreviewTitle>
                  {sharePreviewType === 'kakao' ? t('common.shareModal.kakaoOption') : t('common.shareModal.emailOption')}
                </SharePreviewTitle>
                <SharePreviewCloseButton onClick={handleCloseSharePreview} aria-label="close">
                  ✕
                </SharePreviewCloseButton>
              </SharePreviewTitleRow>
              <SharePreviewDesc>
                {sharePreviewType === 'kakao' ? '카카오톡으로 새 소식을 전합니다.' : '이메일로 새 소식을 전합니다.'}
              </SharePreviewDesc>
            </SharePreviewHeader>

            <SharePreviewBody>
              <SharePreviewImage
                src={sharePreviewType === 'kakao' ? kakaoExampleImg : emailExampleImg}
                alt={sharePreviewType === 'kakao' ? t('common.shareModal.kakaoAlt') : t('common.shareModal.emailAlt')}
              />
            </SharePreviewBody>

            <SharePreviewFooter>
              <SharePreviewBackButton onClick={handleBackSharePreview}>
                {t('common.actions.back')}
              </SharePreviewBackButton>
              <SharePreviewSendButton onClick={handleConfirmSharePreview}>
                보내기
              </SharePreviewSendButton>
            </SharePreviewFooter>
          </SharePreviewSheet>
        )}
      </DSBottomSheet>

      <BottomTabBar>
        <BottomTab type="button" $active>
          <BottomTabIcon src={postActiveIcon} alt="" />
          {t('page.postManagement.pageTitle')}
        </BottomTab>
        <BottomTab type="button" disabled>
          <BottomTabIcon src={sponsorIcon} alt="" />
          {t('page.donorManagement.pageTitle')}
        </BottomTab>
        <BottomTab type="button" disabled>
          <BottomTabIcon src={payIcon} alt="" />
          {t('page.paymentStatus.pageTitle')}
        </BottomTab>
        <BottomTab type="button" onClick={() => navigate(PATHS.PROJECT_SETTINGS, { state: { projectId: currentProject?.project_id } })}>
          <BottomTabIcon src={projectIcon} alt="" />
          {t('page.projectManagement.manage.settings')}
        </BottomTab>
      </BottomTabBar>
    </MobilePageLayout>
  );
};

const BottomTabBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${convertToVwMob(84)};
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 100;
`;

const BottomTab = styled.button<{ $active?: boolean }>`
  flex: 1;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(4)};
  cursor: pointer;

  color: ${({ $active }) => ($active ? '#2BC2E1' : '#BCBCBC')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const BottomTabIcon = styled.img`
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  object-fit: contain;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeaderSection = styled.div`
  margin-bottom: ${convertToVwMob(20)};
`;

const SuccessAlert = styled.div`
  display: flex;
  width: calc(100% + ${convertToVwMob(40)});
  padding: ${convertToVwMob(10)};
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(10)};
  color: #17171B;
  ${({ theme }) => theme.typography.contents.regular20}
  background: ${({ theme }) => theme.colors.primaryBlue[10]};
  margin-top: -${convertToVwMob(40)};
  margin-bottom: ${convertToVwMob(24)};
  margin-left: -${convertToVwMob(20)};
  margin-right: -${convertToVwMob(20)};
  box-sizing: border-box;
`;

const CheckIconImage = styled.img`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  object-fit: contain;
  flex-shrink: 0;
`;

const PageTitle = styled.h1`
  color: #000000;
  font-size: ${convertToVwMob(24)};
  font-weight: 700;
  line-height: 150%;
  margin: 0;
`;

// 게시물이 없을 때 사용하는 컴포넌트들
const ContentCard = styled.div`
  display: flex;
  padding: ${convertToVwMob(24)} ${convertToVwMob(31)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(20)};
  margin-bottom: ${convertToVwMob(10)};
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardText = styled.div`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  line-height: 1.7;
  text-align: left;
  margin-bottom: ${convertToVwMob(29)};
`;

const HighlightText = styled.span`
  color: #2BC2E1;
  ${({ theme }) => theme.typography.contents.regular20}
  font-weight: 600;
`;

const PrimaryButton = styled.button`
  display: flex;
  width: ${convertToVwMob(298)};
  padding: ${convertToVwMob(14)} ${convertToVwMob(22)};
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(8)};
  border-radius: ${convertToVwMob(50)};
  background: #026478;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  line-height: 150%;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 5.8px 0 #357B89;
  }

  &:focus,
  &:active {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const NoPlusIcon = styled.img`
  width: ${convertToVwMob(14)};
  height: ${convertToVwMob(14)};
  object-fit: fill;
`;

const InfoBanner = styled.div`
  display: flex;
  width: ${convertToVwMob(353)};
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(20)};
  background: ${({ theme }) => theme.colors.yellow[5]};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.contents.regular20}
`;

const InfoIconLarge = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  object-fit: fill;
  flex-shrink: 0;
`;

const PlusIcon = styled.img`
  width: ${convertToVwMob(14)};
  height: ${convertToVwMob(14)};
  object-fit: fill;
`;

const YellowBanner = styled.div`
  background: ${({ theme }) => theme.colors.yellow[5]};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  border-radius: ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.colors.yellow[10]};
  gap: ${convertToVwMob(20)};
  margin-bottom: ${convertToVwMob(16)};
`;

const BannerContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwMob(12)};
`;

const BannerIcon = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  object-fit: contain;
  flex-shrink: 0;
`;

const BannerText = styled.p`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 150%;
  font-size: ${convertToVwMob(20)};
  flex: 1;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(10)};
  background: ${({ theme }) => theme.colors.yellow[20]};
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  border-radius: ${convertToVwMob(50)};
  padding: ${convertToVwMob(10)} ${convertToVwMob(40)};
  cursor: pointer;
  width: ${convertToVwMob(305)};
  height: ${convertToVwMob(50)};
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  line-height: 150%;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.yellow[30]};
    box-shadow: 0px 0px 5.4px 0px;
  }

  &:active {
    background: ${({ theme }) => theme.colors.yellow[40]};
  }
`;

const ShareIcon = styled.img`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  object-fit: contain;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  padding-bottom: ${convertToVwMob(80)};
`;

const PostCard = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(20)};
  overflow: hidden;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
`;

const PostHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${convertToVwMob(16)} ${convertToVwMob(16)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const PostDate = styled.span`
  color: #676767;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;

const MoreButton = styled.button`
  background: none;
  border: none;
  font-size: ${convertToVwMob(24)};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  cursor: pointer;
  padding: 0;
  line-height: 1;
`;

const PostImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PostContent = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(16)};
`;

const PostText = styled.div<{ isExpanded: boolean }>`
  color: ${({ theme }) => theme.colors.grayScale[90]};
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 1.5;

  ${({ isExpanded }) =>
    !isExpanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;

    figure[data-embed="story-image"] {
      display: none;
    }
  `}

  ${({ isExpanded }) =>
    isExpanded &&
    `
    figure[data-embed="story-image"] {
      display: block;
    }
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${convertToVwMob(12)} ${convertToVwMob(11)} ${convertToVwMob(60)} ${convertToVwMob(11)};
`;

const DetailButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(216)};
  height: ${convertToVwMob(48)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid #C4C4C4;
  border-radius: ${convertToVwMob(30)};
  gap: ${convertToVwMob(12)};
  ${({ theme }) => theme.typography.contents.regular20}
  color: #1A1A1A;
  cursor: pointer;
  transition: background 0.2s;
`;

const DownIcon = styled.img`
  width: ${convertToVwMob(12)};
  height: ${convertToVwMob(12)};
  object-fit: contain;
`;

const FixedAddButton = styled.button`
  position: fixed;
  bottom: ${convertToVwMob(110)};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  width: ${convertToVwMob(298)};
  padding: ${convertToVwMob(14)} ${convertToVwMob(22)};
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(8)};
  border: none;
  border-radius: 50px;
  background: var(--PrimaryBlue-90, #026478);
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.grayScale[0]};
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 50;


  &:focus,
  &:active {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    transform: translateX(-50%) scale(0.95);
  }
`;

// 바텀시트 관련 스타일
const BottomSheetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${convertToVwMob(12)};
  padding: 0 ${convertToVwMob(16)} ${convertToVwMob(12)};
  background: transparent;
`;

const BottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: ${convertToVwMob(20)};
  overflow: hidden;
  border: 1px solid #EEE;
`;

const BottomSheetButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(56)};
  padding: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  color: #404040;
  ${({ theme }) => theme.typography.button.regular20}
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:active {
    background: #EEEEEE;
  }
`;

const ButtonDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #EEEEEE;
`;

const CancelContainer = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: ${convertToVwMob(20)};
  overflow: hidden;
  border: 1px solid #EEE;
`;

const CancelButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(56)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  color: #404040;
  ${({ theme }) => theme.typography.button.regular20}
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:active {
    background: ${({ theme }) => theme.colors.grayScale[10]};
  }
`;

// 공유 바텀시트 스타일
const ShareBottomSheetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${convertToVwMob(20)};
`;

const ShareHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${convertToVwMob(24)};
  margin-bottom: ${convertToVwMob(24)};
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${convertToVwMob(12)};
`;

const ShareTitle = styled.h2`
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.head.semiBold24}
  margin: 0;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  font-size: ${convertToVwMob(36)};
  font-weight: 300;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(44)};
  height: ${convertToVwMob(44)};
  line-height: 1;
  cursor: pointer;
`;

const ShareDescription = styled.p`
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.regular20}
  margin: 0;
  padding: 0;
  text-align: left;
`;

const ShareOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(20)};
  padding: 0 ${convertToVwMob(24)};
`;

const ShareOption = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${convertToVwMob(20)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
`;

const KakaoIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
`;

const EmailIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  text-align: left;
  flex: 1;
`;

const CopyLinkButtonContainer = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(30)};
  background: #F8F8F8;
`;

const CopyLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  width: 100%;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid #929292;
  border-radius: ${convertToVwMob(10)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.contents.medium20}
  cursor: pointer;
`;

const CopyIcon = styled.div`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z' fill='%2317171B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const SharePreviewSheet = styled.div`
  width: 100%;
  border-top-left-radius: ${convertToVwMob(24)};
  border-top-right-radius: ${convertToVwMob(24)};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewHeader = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  padding: ${convertToVwMob(18)} ${convertToVwMob(20)} ${convertToVwMob(14)};
`;

const SharePreviewTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(12)};
`;

const SharePreviewTitle = styled.h3`
  margin: 0;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const SharePreviewCloseButton = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.grayScale[50]};
  font-size: ${convertToVwMob(32)};
  line-height: 1;
  padding: 0;
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SharePreviewDesc = styled.p`
  margin: ${convertToVwMob(10)} 0 0;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const SharePreviewBody = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(20)} ${convertToVwMob(18)};
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewImage = styled.img`
  width: 100%;
  max-width: ${convertToVwMob(240)};
  object-fit: contain;
`;

const SharePreviewFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  padding: ${convertToVwMob(14)} ${convertToVwMob(20)} ${convertToVwMob(20)};
  display: flex;
  gap: ${convertToVwMob(8)};
  justify-content: flex-end;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewBackButton = styled.button`
  height: ${convertToVwMob(44)};
  min-width: ${convertToVwMob(92)};
  padding: 0 ${convertToVwMob(18)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[30]};
  border-radius: ${convertToVwMob(6)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.regular16}
  cursor: pointer;
`;

const SharePreviewSendButton = styled.button`
  height: ${convertToVwMob(44)};
  min-width: ${convertToVwMob(92)};
  padding: 0 ${convertToVwMob(20)};
  border: none;
  border-radius: ${convertToVwMob(6)};
  background: #0a667f;
  color: #fff;
  ${({ theme }) => theme.typography.contents.regular16}
  cursor: pointer;
`;

export default PostManagementMob;
